import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Truck,
  CreditCard,
  Banknote,
  Wallet,
  Clock,
  CheckCircle2,
  XCircle,
  PackageCheck,
  ChefHat,
  Bike,
  Navigation,
  Ban,
  RotateCcw,
  Smartphone,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import DeliveryTrackingMap from '../components/DeliveryTrackingMap';
import SectionCard from '../components/SectionCard';
import Modal from '../components/Modal';
import usePageMetadata from '../hooks/usePageMetadata';
import * as ordersApi from '../api/endpoints/orders';
import { containerNarrow, pageSection } from '../styles/ui';

// Refresh cadence for the driver marker while an order is out for delivery.
// Comfortably under the backend's 120s staleness window so the marker is
// replaced well before the fix it shows would be considered stale.
const TRACKING_POLL_MS = 15000;

// Mirrors DELIVERY_TRACKING_STALE_AFTER_SECONDS in the backend's
// deliveryTrackingService.js; only used when a tracking payload arrives
// without its own `staleAfterSeconds`.
const DEFAULT_TRACKING_STALE_AFTER_SECONDS = 120;

// Labels/icons for the paymentMethod values the customer-app order API
// accepts ('online' | 'cod', plus fully wallet-settled orders).
const PAYMENT_METHOD_META = {
  cod: { label: 'Cash on Delivery', icon: Banknote },
  online: { label: 'Online Payment', icon: Smartphone },
  wallet: { label: 'Wallet', icon: Wallet },
};

// Sale.paymentStatus enum (see backend models/Sale.js).
const PAYMENT_STATUS_LABEL = {
  pending: 'Pending',
  partial: 'Partially Paid',
  completed: 'Paid',
  processing: 'Confirming payment…',
  refunded: 'Refunded',
  refund_pending: 'Refund in progress',
};

// Single source of truth for a fulfillment status' label and icon. Keys are
// the values the backend records in fulfillment.statusHistory (the
// fulfillment.status enum in models/Sale.js, plus `assigned`, which the
// customer-app projection derives from driverAssignment, and the packing
// aliases it recognises).
const STATUS_META = {
  placed: { label: 'Order Placed', icon: CheckCircle2 },
  confirmed: { label: 'Confirmed', icon: CheckCircle2 },
  preparing: { label: 'Packed', icon: ChefHat },
  packed: { label: 'Packed', icon: ChefHat },
  allocated: { label: 'Packed', icon: ChefHat },
  partially_allocated: { label: 'Partially Packed', icon: ChefHat },
  assigned: { label: 'Assigned', icon: Bike },
  out_for_delivery: { label: 'Out for Delivery', icon: Truck },
  delivered: { label: 'Delivered', icon: PackageCheck },
  failed: { label: 'Failed', icon: XCircle },
  cancelled: { label: 'Cancelled', icon: Ban },
};

// "out_for_delivery" -> "Out For Delivery". Fallback for a status the backend
// starts emitting before this map learns about it — a real new stage should
// still read sensibly rather than render as a blank step.
const titleCaseStatus = (status) =>
  status.replace(/[_-]+/g, ' ').trim().replace(/\b\w/g, (char) => char.toUpperCase());

// Turns one raw history entry into something renderable, or null if it carries
// no usable event. Guards against the malformed rows that used to reach here:
// null/undefined, non-objects, and entries with a missing or blank status.
const normalizeStatusEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const status = typeof entry.status === 'string' ? entry.status.trim() : '';
  if (!status) return null;

  const meta = STATUS_META[status];
  const label = meta ? meta.label : titleCaseStatus(status);
  if (!label) return null;

  const note = typeof entry.note === 'string' ? entry.note.trim() : '';
  return {
    status,
    label,
    icon: meta ? meta.icon : CheckCircle2,
    at: entry.at || null,
    note,
  };
};

// Ordered list of real events to render. Drops unusable entries and exact
// repeats (same status at the same instant); the same status at a *different*
// time is a genuine re-transition and is kept.
const buildStatusTimeline = (history) => {
  if (!Array.isArray(history)) return [];
  return history
    .map(normalizeStatusEntry)
    .filter(Boolean)
    .filter((entry, index, all) => {
      const previous = all[index - 1];
      return !previous || previous.status !== entry.status || String(previous.at) !== String(entry.at);
    })
    .map((entry, index) => ({ ...entry, key: `${entry.status}-${entry.at || index}` }));
};

const formatCurrency = (value) => (value != null ? `₹${value}` : '₹0');

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

// How old the driver's last position fix is *right now*. The backend stamps
// `ageSeconds` when it responds, so prefer recomputing from `updatedAt` and
// fall back to ageSeconds plus the time since we fetched it. null means
// "unknown", which callers treat as stale — matching the backend's own rule.
const getTrackingAgeSeconds = (tracking, checkedAt) => {
  const location = tracking?.driverLocation;
  if (!location) return null;
  if (location.updatedAt) {
    const updatedAt = new Date(location.updatedAt).getTime();
    if (!Number.isNaN(updatedAt)) return Math.max(0, Math.floor((Date.now() - updatedAt) / 1000));
  }
  if (typeof location.ageSeconds === 'number') {
    const sinceFetch = checkedAt ? Math.max(0, Math.floor((Date.now() - checkedAt) / 1000)) : 0;
    return location.ageSeconds + sinceFetch;
  }
  return null;
};

// Cancellation is intentionally not offered here: the backend's customer-app
// router (src/routes/customerApp.js) has no order-cancel endpoint today, only
// a separate order-requests/return-exchange flow this page doesn't cover.
const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [tracking, setTracking] = useState(null);
  const [trackingPoll, setTrackingPoll] = useState({ failed: false, checkedAt: Date.now() });
  // Returns/exchanges aren't built into this website yet, even though the
  // backend already supports them (see order.availableActions.canReturn) —
  // this popup is the interim guidance until that flow is built here.
  const [showReturnExchangeModal, setShowReturnExchangeModal] = useState(false);

  usePageMetadata({
    title: 'Order Details | Fresh & Homely',
    description: 'View your Fresh & Homely order details and delivery status.',
    canonicalPath: `/orders/${id}`,
  });

  const load = () => {
    setState({ status: 'loading', data: null, error: null });
    ordersApi
      .getOrder(id)
      .then((response) => setState({ status: 'success', data: response.data, error: null }))
      .catch((err) => setState({ status: 'error', data: null, error: err.message }));
  };

  useEffect(load, [id]);

  // Live tracking only applies while the order is actively moving toward the
  // customer — matches the fulfillment statuses the backend itself treats as
  // trackable (see isDeliveryLiveTrackable in deliveryTrackingService.js).
  // Poll while out for delivery so the driver's marker keeps moving on the
  // map; other statuses only need the one-off fetch for the ETA/ready state.
  useEffect(() => {
    setTracking(null);
    setTrackingPoll({ failed: false, checkedAt: Date.now() });
    if (state.status !== 'success') return undefined;
    const fulfillmentStatus = state.data?.fulfillment?.status;
    if (!['out_for_delivery', 'confirmed', 'preparing', 'assigned'].includes(fulfillmentStatus)) return undefined;

    let cancelled = false;
    const poll = () => {
      ordersApi.getOrderTracking(id).then((res) => {
        if (!cancelled) {
          setTracking(res.data);
          setTrackingPoll({ failed: false, checkedAt: Date.now() });
        }
      }).catch(() => {
        if (!cancelled) setTrackingPoll({ failed: true, checkedAt: Date.now() });
      });
    };

    poll();
    const intervalId = fulfillmentStatus === 'out_for_delivery' ? setInterval(poll, TRACKING_POLL_MS) : null;
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [state.status, state.data, id]);

  const order = state.data;
  const fulfillment = order?.fulfillment || {};
  const deliveryAddress = order?.deliveryAddress || fulfillment.deliveryAddress || null;
  const deliverySlot = order?.deliverySlot || fulfillment.deliverySlot || null;
  const statusTimeline = buildStatusTimeline(order?.statusHistory || fulfillment.statusHistory);
  const paymentMeta = order ? PAYMENT_METHOD_META[order.paymentMethod] : null;
  const currentFulfillmentStatus = fulfillment.status || order?.status || 'placed';
  const trackingAgeSeconds = getTrackingAgeSeconds(tracking, trackingPoll.checkedAt);
  const trackingStaleAfterSeconds = tracking?.staleAfterSeconds || DEFAULT_TRACKING_STALE_AFTER_SECONDS;
  const trackingIsStale = Boolean(
    tracking?.driverLocation
    && (tracking.driverLocation.stale
      || trackingAgeSeconds === null
      || trackingAgeSeconds > trackingStaleAfterSeconds),
  );
  const liveTrackingAvailable = Boolean(tracking?.liveTrackingAvailable && !trackingIsStale);
  const isFailureStatus = ['failed', 'cancelled'].includes(currentFulfillmentStatus);

  return (
    <>
      <Navbar />
      <section className={pageSection}>
        <div className={containerNarrow}>
          {state.status === 'loading' && <LoadingState label="Loading order..." />}
          {state.status === 'error' && <ErrorState message={state.error} onRetry={load} />}
          {state.status === 'success' && order && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
                <h1 className="serif text-3xl sm:text-4xl text-emerald-950">
                  {order.invoiceNumber || `Order ${order._id}`}
                </h1>
                <span className={`text-sm font-medium capitalize px-3 py-1.5 rounded-full shadow-soft ${
                  isFailureStatus ? 'text-red-700 bg-red-50' : 'text-success-700 bg-success-50'
                }`}>
                  {currentFulfillmentStatus.replace(/_/g, ' ')}
                </span>
              </div>

              {order.deliveryCode?.active && order.deliveryCode?.code && (
                <div className="glass-card rounded-2xl p-5 mb-6">
                  <p className="text-sm text-emerald-800 mb-1">Share this code with your delivery driver</p>
                  <p className="text-2xl font-bold text-emerald-950 tracking-widest">{order.deliveryCode.code}</p>
                  {order.deliveryCode.expiresAt && (
                    <p className="text-xs text-emerald-700 mt-1">
                      Expires {formatDateTime(order.deliveryCode.expiresAt)}
                    </p>
                  )}
                </div>
              )}

              {tracking && (
                <SectionCard title="Delivery Tracking" icon={Navigation}>
                  {liveTrackingAvailable ? (
                    <DeliveryTrackingMap
                      driverLocation={tracking.driverLocation}
                      destinationCoordinates={tracking.destinationCoordinates}
                      storeCoordinates={tracking.storeCoordinates}
                      route={tracking.route}
                    />
                  ) : (
                    <p className="text-sm text-ink-secondary">
                      {trackingIsStale
                        ? "We haven't had a location update from the driver recently."
                        : 'Live tracking will appear here once your order is on its way.'}
                    </p>
                  )}
                  {tracking.eta && <p className="text-sm text-ink-secondary mt-3">Estimated arrival: {tracking.eta}</p>}
                  {tracking.driver?.name && <p className="text-sm text-ink-secondary">Driver: {tracking.driver.name}</p>}
                </SectionCard>
              )}

              {statusTimeline.length > 0 && (
                <SectionCard title="Order Status" icon={Clock}>
                  {/* Events that actually happened, newest last — not a
                      forward-looking progress tracker, so stages the order has
                      not reached are deliberately absent. */}
                  <ol className="space-y-5">
                    {statusTimeline.map((entry, index) => {
                      const StatusIcon = entry.icon;
                      const isLatest = index === statusTimeline.length - 1;
                      const timestamp = formatDateTime(entry.at);
                      return (
                        <li key={entry.key} className="relative flex gap-3">
                          {!isLatest && (
                            // Connector runs from under this marker to the next
                            // one; -bottom-5 bridges the space-y-5 gap.
                            <span
                              aria-hidden="true"
                              className="absolute left-[11px] top-6 -bottom-5 w-px bg-emerald-100"
                            />
                          )}
                          <span
                            aria-hidden="true"
                            className={`relative z-10 shrink-0 flex h-6 w-6 items-center justify-center rounded-full ${
                              isLatest ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-600'
                            }`}
                          >
                            <StatusIcon size={14} />
                          </span>
                          <div className="min-w-0">
                            <p className={`text-sm ${isLatest ? 'font-semibold text-emerald-900' : 'font-medium text-emerald-950'}`}>
                              {entry.label}
                            </p>
                            {timestamp && <p className="text-xs text-ink-secondary mt-0.5">{timestamp}</p>}
                            {entry.note && <p className="text-xs text-ink-secondary mt-0.5">{entry.note}</p>}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </SectionCard>
              )}

              <SectionCard title="Items" icon={PackageCheck}>
                <div className="space-y-3">
                  {(order.items || []).map((item, index) => (
                    <div key={item._id || index} className="flex justify-between text-sm">
                      <span className="text-gray-700">
                        {item.productName} × {item.quantity}
                      </span>
                      <span className="font-medium text-emerald-950">{formatCurrency(item.lineTotal)}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-1.5 text-sm pt-4 mt-4 border-t border-emerald-50">
                  <div className="flex justify-between text-ink-secondary">
                    <span>Subtotal</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                  {order.totalTax > 0 && (
                    <div className="flex justify-between text-ink-secondary">
                      <span>Tax</span>
                      <span>{formatCurrency(order.totalTax)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-ink-secondary">
                    <span>Delivery Fee</span>
                    <span>{order.deliveryFee > 0 ? formatCurrency(order.deliveryFee) : 'Free'}</span>
                  </div>
                  {order.platformCharge > 0 && (
                    <div className="flex justify-between text-ink-secondary">
                      <span>Platform Charge</span>
                      <span>{formatCurrency(order.platformCharge)}</span>
                    </div>
                  )}
                  {order.handlingCharge > 0 && (
                    <div className="flex justify-between text-ink-secondary">
                      <span>Handling Charge</span>
                      <span>{formatCurrency(order.handlingCharge)}</span>
                    </div>
                  )}
                  {order.walletAmountUsed > 0 && (
                    <div className="flex justify-between text-ink-secondary">
                      <span>Paid via Wallet</span>
                      <span>-{formatCurrency(order.walletAmountUsed)}</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-between font-bold text-emerald-950 pt-3 mt-3 border-t border-emerald-50">
                  <span>Total</span>
                  <span>{formatCurrency(order.grandTotal)}</span>
                </div>
              </SectionCard>

              {/* Guarded on the resolved `deliveryAddress`, not on
                  fulfillment.deliveryAddress: the address can also arrive at
                  the order root, in which case the old guard hid a section
                  whose data was present. */}
              {deliveryAddress && (
                <SectionCard title="Delivery Address" icon={MapPin}>
                  <p className="text-sm text-ink-secondary">
                    {[
                      deliveryAddress.line1,
                      deliveryAddress.line2,
                      deliveryAddress.city,
                      deliveryAddress.state,
                      deliveryAddress.pincode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {deliveryAddress.landmark && (
                    <p className="text-xs text-ink-secondary mt-1">Landmark: {deliveryAddress.landmark}</p>
                  )}
                </SectionCard>
              )}

              {/* Delivery Time Slot */}
              {deliverySlot && (
                <SectionCard title="Delivery Time Slot" icon={Clock}>
                  <p className="text-sm font-semibold text-emerald-950">
                    {deliverySlot.label || `${deliverySlot.startTime} - ${deliverySlot.endTime}`}
                  </p>
                  {deliverySlot.selectedAt && (
                    <p className="text-xs text-ink-secondary mt-1">
                      Selected {formatDateTime(deliverySlot.selectedAt)}
                    </p>
                  )}
                </SectionCard>
              )}

              {/* Payment Method */}
              <SectionCard title="Payment Method" icon={paymentMeta?.icon || CreditCard}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-emerald-950">
                    {paymentMeta?.label || order.paymentMethod || 'Not specified'}
                  </p>
                  {order.paymentStatus && (
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                        order.paymentStatus === 'completed'
                          ? 'text-success-700 bg-success-50'
                          : order.paymentStatus === 'refunded' || order.paymentStatus === 'refund_pending'
                            ? 'text-red-700 bg-red-50'
                            : 'text-amber-700 bg-amber-50'
                      }`}
                    >
                      {PAYMENT_STATUS_LABEL[order.paymentStatus] || order.paymentStatus}
                    </span>
                  )}
                </div>
              </SectionCard>

              {order.availableActions?.canReturn && (
                <button
                  type="button"
                  onClick={() => setShowReturnExchangeModal(true)}
                  className="w-full mb-3 flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold py-3.5 shadow-soft hover:bg-emerald-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
                >
                  <RotateCcw size={18} aria-hidden="true" />
                  Return / Exchange
                </button>
              )}

              {order.availableActions?.canCancel && (
                <button
                  type="button"
                  onClick={() => navigate(`/orders/${id}/cancel`, { state: { order } })}
                  className="w-full mb-6 flex items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 text-red-700 font-semibold py-3.5 shadow-soft hover:bg-red-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2"
                >
                  <Ban size={18} aria-hidden="true" />
                  Cancel Order
                </button>
              )}

              <Modal
                open={showReturnExchangeModal}
                onClose={() => setShowReturnExchangeModal(false)}
                title="Return or Exchange"
              >
                <div className="p-6 flex flex-col items-center text-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
                    <Smartphone size={22} className="text-emerald-700" aria-hidden="true" />
                  </div>
                  <p className="text-sm text-ink-secondary leading-relaxed">
                    For returns or exchanges, please download the Fresh &amp; Homely app to proceed.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowReturnExchangeModal(false)}
                    className="mt-2 w-full rounded-full bg-gradient-primary text-white font-semibold py-2.5 shadow-glow-teal transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2"
                  >
                    Got it
                  </button>
                </div>
              </Modal>
            </>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default OrderDetailPage;
